import { archiveProjectResponseSchema, createProjectResponseSchema, listProjectsResponseSchema, projectSchema, updateProjectResponseSchema } from "../http/projects.js";
import { localProjectControlConnectionSchema } from "../daemon/local-project-control.js";
import { agentActivitySnapshotResponseSchema } from "../http/agent-activity.js";
import { createCenterAgentRoutineResponseSchema, deleteCenterAgentRoutineResponseSchema, listCenterAgentRoutineRunsResponseSchema, listCenterAgentRoutinesResponseSchema, pauseCenterAgentRoutineResponseSchema, resumeCenterAgentRoutineResponseSchema, updateCenterAgentRoutineResponseSchema } from "../http/center-agent-routines.js";
import { agentActivitySessionInventoryResponseSchema } from "../http/agent-activity-sessions.js";
import { organizationComputersResponseSchema } from "../http/organization-computers.js";
import { spaceMemberAgentWakeAvailabilitySchema, spaceMemberDirectoryAllowedRelationsSchema, spaceMemberDirectorySchema, spaceMemberKindSchema } from "../http/space-members.js";
import { getOrganizationSkillResponseSchema, listOrganizationSkillsResponseSchema, organizationSkillReleaseSummarySchema, organizationSkillSummarySchema } from "../http/organization-skills.js";
import { daemonStatusServerRequirementSchema } from "./daemon-status-server-requirement.js";
import { serviceControlCapabilitiesSchema } from "../../service-account-admission.js";
import { submitFeedbackIssueRequestSchema, submitFeedbackIssueResponseSchema } from "../http/feedback.js";
import { organizationDirectoryResponseSchema } from "../http/organization-directory.js";
import { getOrganizationJoinLinkResponseSchema, revokeOrganizationJoinLinkResponseSchema } from "../http/organization-join-link.js";
import { organizationSpaceDirectoryWithMemberCountsResponseSchema } from "../http/organization-space-directory.js";
import * as z$1 from "zod/v4";
//#region src/schemas/cli/json-output.ts
/**
* Shared contract for the OpenMeld CLI `--json` machine-readable output boundary.
*
* This is the single source of truth for the stdout envelope a non-interactive
* driver (Desktop GUI, scripts, CI) parses when a command is run with `--json`.
* The CLI must derive its envelope types from these schemas via `z.infer`
* instead of hand-writing a parallel shape that can drift
* (`developing/architecture/shared-contracts.md`: "CLI JSON" is a shared
* boundary; "Do not create a separate hand-written shared type that can drift
* from the runtime schema.").
*
* Scope: this owns ONLY the `{ schemaVersion: 1, type, ... }` `--json` envelope.
* It does NOT own the separate `--view agent` presenter protocol (the flat
* `{ type: "error", code, message }` / `{ type: "hints", ... }` lines and the
* `{ type: "result", code, payload }` stream), which is a distinct
* machine-readable surface emitted without `schemaVersion`.
*/
/**
* The CLI `--json` envelope schema version. This versions the envelope wire
* surface independently of `OPENMELD_SCHEMA_VERSION` and the other component schema
* versions, mirroring `LOCAL_COMPONENTS_SCHEMA_VERSION`, because the `--json`
* boundary evolves on its own cadence. It is currently `1` everywhere the CLI
* emits an envelope.
*/
const CLI_JSON_OUTPUT_SCHEMA_VERSION = 1;
const schemaVersionSchema = z$1.literal(1);
/**
* Stable error codes a blocked command entry-check emits under `--json`. These
* are the closed set the CLI routes blocked entry states to; defining the set
* here keeps the CLI from minting drift codes. The error envelope itself stays
* open (`error.code` is any string) because errors also originate outside the
* entry-check flow (e.g. `cli.error`, gateway codes).
*/
const cliJsonEntryCheckBlockedErrorCodeSchema = z$1.enum([
	"auth.authentication_required",
	"auth.session_token_unavailable",
	"profile.required",
	"local_state.repair_required",
	"upgrade.required"
]);
/**
* The failure envelope: the top-level boundary prints exactly this when a
* command throws (or a blocking entry-check fires) under `--json`, then exits 1.
* Fully owned, so it is strict.
*/
const cliJsonErrorEnvelopeSchema = z$1.strictObject({
	schemaVersion: schemaVersionSchema,
	type: z$1.literal("error"),
	error: z$1.strictObject({
		code: z$1.string(),
		message: z$1.string(),
		requestId: z$1.string().optional()
	})
});
/**
* Success envelopes share the `{ schemaVersion, type }` frame and then flatten a
* command-specific result onto the envelope. The deep result shapes (daemon
* install/run/status state, runtime projections, agent registration summaries)
* are owned by their respective daemon / agents runtime contracts. This envelope
* is the FRAME owner: it pins the version literal and the closed `type`
* discriminant, and deliberately RELAXES the flattened payload via
* `z.looseObject` rather than re-deriving those domain-owned shapes here — doing
* so would create a second source of truth for payloads another owner already
* defines. (`shared-contracts.md`: "Document any relaxed object boundary.")
*
* Only fields the CLI authors directly on the envelope (and that carry a stable
* literal or primitive shape) are validated here; everything flattened from a
* domain payload passes through the relaxed boundary.
*/
function defineSuccessEnvelope(type, shape) {
	return z$1.looseObject({
		schemaVersion: schemaVersionSchema,
		type: z$1.literal(type),
		...shape
	});
}
function defineStrictSuccessEnvelope(type, shape) {
	return z$1.strictObject({
		schemaVersion: schemaVersionSchema,
		type: z$1.literal(type),
		...shape
	});
}
const cliJsonDaemonRunEnvelopeSchema = defineSuccessEnvelope("daemon.run", { ok: z$1.boolean() });
const cliJsonDaemonInstallEnvelopeSchema = defineSuccessEnvelope("daemon.install", { status: z$1.string() });
const cliJsonDaemonUninstallEnvelopeSchema = defineSuccessEnvelope("daemon.uninstall", { status: z$1.enum(["removed", "not_installed"]) });
const cliJsonDaemonReinstallEnvelopeSchema = defineSuccessEnvelope("daemon.reinstall", {});
const cliJsonDaemonStatusEnvelopeSchema = defineSuccessEnvelope("daemon.status", {
	runtimeFlavor: z$1.enum(["binary", "node-bundle"]).optional(),
	serverRequirement: daemonStatusServerRequirementSchema.optional()
});
const cliJsonServiceControlCapabilitiesEnvelopeSchema = defineSuccessEnvelope("service.control_capabilities", serviceControlCapabilitiesSchema.shape);
const cliJsonDaemonStopEnvelopeSchema = defineSuccessEnvelope("daemon.stop", {});
const cliJsonDaemonAutostartEnvelopeSchema = defineSuccessEnvelope("daemon.autostart", {
	action: z$1.enum(["enable", "disable"]),
	enabled: z$1.boolean()
});
const cliJsonAgentsDetectEnvelopeSchema = defineSuccessEnvelope("agents.detect", { count: z$1.number().int().nonnegative() });
const cliJsonAgentsListEnvelopeSchema = defineSuccessEnvelope("agents.list", { count: z$1.number().int().nonnegative() });
const cliJsonAgentsShowEnvelopeSchema = defineSuccessEnvelope("agents.show", {});
const cliJsonAgentsEnableEnvelopeSchema = defineSuccessEnvelope("agents.enable", {});
const cliJsonAgentsDisableEnvelopeSchema = defineSuccessEnvelope("agents.disable", {});
const cliJsonFeedbackSubmittedEnvelopeSchema = defineSuccessEnvelope("feedback_submitted", {
	issueId: submitFeedbackIssueResponseSchema.shape.issueId,
	category: submitFeedbackIssueRequestSchema.shape.category,
	submitterKind: submitFeedbackIssueRequestSchema.shape.submitterKind,
	anonymous: z$1.boolean()
});
const cliJsonAgentActivityLocalBindingSchema = z$1.strictObject({
	boundAtMs: z$1.number().int().nonnegative(),
	organizationId: z$1.string().min(1),
	projectId: z$1.string().min(1),
	projectSlug: z$1.string().min(1),
	repositoryFingerprint: z$1.string().nullable(),
	repositoryPath: z$1.string().min(1)
});
const cliJsonProjectsListEnvelopeSchema = defineStrictSuccessEnvelope("projects.list", {
	...listProjectsResponseSchema.shape,
	localBindings: z$1.array(cliJsonAgentActivityLocalBindingSchema)
});
const cliJsonProjectsCreateEnvelopeSchema = defineStrictSuccessEnvelope("projects.create", createProjectResponseSchema.shape);
const cliJsonProjectsUpdateEnvelopeSchema = defineStrictSuccessEnvelope("projects.update", updateProjectResponseSchema.shape);
const cliJsonProjectsArchiveEnvelopeSchema = defineStrictSuccessEnvelope("projects.archive", archiveProjectResponseSchema.shape);
const cliJsonAgentActivityIntegrationUpdateSchema = z$1.strictObject({
	claude: z$1.enum([
		"changed",
		"missing",
		"reused"
	]),
	codex: z$1.enum([
		"changed",
		"missing",
		"reused"
	]),
	cursor: z$1.enum([
		"changed",
		"missing",
		"reused"
	]),
	openCode: z$1.enum([
		"changed",
		"missing",
		"reused"
	])
});
const cliJsonProjectsBindEnvelopeSchema = defineStrictSuccessEnvelope("projects.bind", {
	binding: cliJsonAgentActivityLocalBindingSchema,
	integrationSetup: z$1.discriminatedUnion("status", [z$1.strictObject({
		integrations: cliJsonAgentActivityIntegrationUpdateSchema,
		status: z$1.literal("ready")
	}), z$1.strictObject({
		message: z$1.string().trim().min(1).max(512),
		status: z$1.literal("needs_attention")
	})]),
	project: projectSchema
});
const cliJsonProjectsUnbindEnvelopeSchema = defineStrictSuccessEnvelope("projects.unbind", {
	organizationId: z$1.string().min(1),
	projectSlug: z$1.string().min(1),
	unbound: z$1.literal(true)
});
const cliJsonProjectConnectionSnapshotShape = {
	organizationId: z$1.string().min(1),
	projectId: z$1.string().min(1),
	projectSlug: z$1.string().min(1),
	connections: z$1.array(localProjectControlConnectionSchema)
};
const cliJsonProjectsConnectionsListEnvelopeSchema = defineStrictSuccessEnvelope("projects.connections.list", { ...cliJsonProjectConnectionSnapshotShape });
const cliJsonProjectsConnectionsSetDefaultEnvelopeSchema = defineStrictSuccessEnvelope("projects.connections.set_default", {
	...cliJsonProjectConnectionSnapshotShape,
	status: z$1.enum(["updated", "unchanged"])
});
const cliJsonProjectsConnectionsRemoveEnvelopeSchema = defineStrictSuccessEnvelope("projects.connections.remove", {
	...cliJsonProjectConnectionSnapshotShape,
	status: z$1.enum(["removed", "unchanged"])
});
const cliJsonAgentActivityReadEnvelopeSchema = defineStrictSuccessEnvelope("activity.read", agentActivitySnapshotResponseSchema.shape);
const cliJsonAgentActivitySessionsEnvelopeSchema = defineStrictSuccessEnvelope("activity.sessions", agentActivitySessionInventoryResponseSchema.shape);
const cliJsonAgentActivityIntegrationStatusSchema = z$1.strictObject({
	claude: z$1.boolean(),
	codex: z$1.boolean(),
	cursor: z$1.boolean(),
	openCode: z$1.boolean()
});
const cliJsonAgentActivityHealthSchema = z$1.strictObject({
	pending: z$1.number().int().nonnegative(),
	permanentlyRejected: z$1.number().int().nonnegative(),
	retrying: z$1.number().int().nonnegative()
});
const cliJsonAgentActivityInstallEnvelopeSchema = defineStrictSuccessEnvelope("activity.install", { integrations: cliJsonAgentActivityIntegrationUpdateSchema });
const cliJsonAgentActivityUninstallEnvelopeSchema = defineStrictSuccessEnvelope("activity.uninstall", { integrations: cliJsonAgentActivityIntegrationUpdateSchema });
const cliJsonAgentActivityStatusEnvelopeSchema = defineStrictSuccessEnvelope("activity.status", {
	controllerTaskHealth: cliJsonAgentActivityHealthSchema,
	health: cliJsonAgentActivityHealthSchema,
	integrations: cliJsonAgentActivityIntegrationStatusSchema
});
const cliJsonAgentActivityHealthEnvelopeSchema = defineStrictSuccessEnvelope("activity.health", {
	controllerTaskHealth: cliJsonAgentActivityHealthSchema,
	health: cliJsonAgentActivityHealthSchema
});
const cliJsonAgentActivitySyncEnvelopeSchema = defineStrictSuccessEnvelope("activity.sync", { result: z$1.strictObject({
	controllerTaskEventSubmittedCount: z$1.number().int().nonnegative(),
	controllerTaskHealth: cliJsonAgentActivityHealthSchema,
	health: cliJsonAgentActivityHealthSchema,
	observedCount: z$1.number().int().nonnegative(),
	reason: z$1.enum([
		"service_contract_unavailable",
		"auth_unavailable",
		"registration_rejected",
		"write_auth_rejected",
		"write_endpoint_unavailable",
		"write_retryable_http_failure",
		"write_response_invalid",
		"write_timeout",
		"write_transport_failure",
		"inventory_write_auth_rejected",
		"inventory_endpoint_unavailable",
		"inventory_write_retryable_http_failure",
		"inventory_write_transport_failure"
	]).nullable(),
	sessionInventory: z$1.strictObject({
		reason: z$1.enum([
			"service_contract_unavailable",
			"auth_unavailable",
			"registration_rejected",
			"inventory_write_auth_rejected",
			"inventory_endpoint_unavailable",
			"inventory_write_retryable_http_failure",
			"inventory_write_transport_failure"
		]).nullable(),
		sessionCount: z$1.number().int().nonnegative(),
		sourceCount: z$1.number().int().nonnegative(),
		status: z$1.enum([
			"idle",
			"submitted",
			"unavailable"
		]),
		submittedSourceCount: z$1.number().int().nonnegative()
	}),
	status: z$1.enum([
		"idle",
		"submitted",
		"unavailable"
	]),
	submittedCount: z$1.number().int().nonnegative()
}) });
const cliJsonAgentActivityContextEnvelopeSchema = defineStrictSuccessEnvelope("activity.context", { context: z$1.string().min(1).max(300).nullable() });
const cliJsonRoutinesListEnvelopeSchema = defineStrictSuccessEnvelope("routines.list", listCenterAgentRoutinesResponseSchema.shape);
const cliJsonRoutinesCreateEnvelopeSchema = defineStrictSuccessEnvelope("routines.create", createCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesShowEnvelopeSchema = defineStrictSuccessEnvelope("routines.show", createCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesUpdateEnvelopeSchema = defineStrictSuccessEnvelope("routines.update", updateCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesPauseEnvelopeSchema = defineStrictSuccessEnvelope("routines.pause", pauseCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesResumeEnvelopeSchema = defineStrictSuccessEnvelope("routines.resume", resumeCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesDeleteEnvelopeSchema = defineStrictSuccessEnvelope("routines.delete", deleteCenterAgentRoutineResponseSchema.shape);
const cliJsonRoutinesRunsEnvelopeSchema = defineStrictSuccessEnvelope("routines.runs", listCenterAgentRoutineRunsResponseSchema.shape);
const cliJsonSpaceMembersEnvelopeSchema = defineStrictSuccessEnvelope("space.members", spaceMemberDirectorySchema.shape);
const cliJsonSpaceMembersBriefEnvelopeSchema = defineStrictSuccessEnvelope("space.members.brief", {
	spaceId: z$1.string().trim().min(1),
	generatedAt: z$1.iso.datetime({ offset: true }),
	members: z$1.array(z$1.strictObject({
		profileId: z$1.string().trim().min(1),
		displayName: z$1.string().trim().min(1),
		profileKind: spaceMemberKindSchema,
		primaryMentionLabel: z$1.string().trim().min(1),
		allowedRelations: spaceMemberDirectoryAllowedRelationsSchema,
		wakeAvailability: spaceMemberAgentWakeAvailabilitySchema.pick({
			action: true,
			phase: true,
			reasonCode: true,
			status: true,
			summary: true
		}).optional(),
		canWakeNow: z$1.boolean().optional()
	}))
});
const cliJsonSpaceListOrganizationEnvelopeSchema = defineStrictSuccessEnvelope("space.list.organization", {
	scope: z$1.literal("organization"),
	organizationId: organizationSpaceDirectoryWithMemberCountsResponseSchema.shape.organizationId,
	generatedAt: organizationSpaceDirectoryWithMemberCountsResponseSchema.shape.generatedAt,
	total: z$1.number().int().nonnegative(),
	returned: z$1.number().int().nonnegative(),
	spaces: organizationSpaceDirectoryWithMemberCountsResponseSchema.shape.spaces
});
const cliJsonOrganizationDirectoryEnvelopeSchema = defineStrictSuccessEnvelope("organization.directory", organizationDirectoryResponseSchema.shape);
const cliJsonOrganizationComputersEnvelopeSchema = defineStrictSuccessEnvelope("organization.computers", organizationComputersResponseSchema.shape);
const cliJsonOrganizationInviteLinkGetEnvelopeSchema = defineStrictSuccessEnvelope("organization.invite_link.get", getOrganizationJoinLinkResponseSchema.shape);
const cliJsonOrganizationInviteLinkCreateEnvelopeSchema = defineStrictSuccessEnvelope("organization.invite_link.create", getOrganizationJoinLinkResponseSchema.shape);
const cliJsonOrganizationInviteLinkRevokeEnvelopeSchema = defineStrictSuccessEnvelope("organization.invite_link.revoke", revokeOrganizationJoinLinkResponseSchema.shape);
const cliJsonSkillsListEnvelopeSchema = defineStrictSuccessEnvelope("skills.list", listOrganizationSkillsResponseSchema.shape);
const cliJsonSkillsShowEnvelopeSchema = defineStrictSuccessEnvelope("skills.show", getOrganizationSkillResponseSchema.shape);
const cliJsonSkillsLoadEnvelopeSchema = defineStrictSuccessEnvelope("skills.load", {
	cachePath: z$1.string().min(1),
	digest: organizationSkillReleaseSummarySchema.shape.digest,
	organizationId: organizationSkillSummarySchema.shape.organizationId,
	releaseId: organizationSkillReleaseSummarySchema.shape.id,
	resourcePaths: z$1.array(z$1.string().min(1)).max(128),
	reused: z$1.boolean(),
	skillId: organizationSkillSummarySchema.shape.id,
	skillMarkdownPath: z$1.string().min(1),
	skillName: organizationSkillSummarySchema.shape.name
});
/**
* The full `--json` output envelope: the failure envelope plus every success
* envelope, discriminated on `type`. A `--json` driver dispatches on
* `schemaVersion` + `type`; that frame is the contract this validates.
*/
const cliJsonOutputEnvelopeSchema = z$1.discriminatedUnion("type", [
	cliJsonErrorEnvelopeSchema,
	cliJsonDaemonRunEnvelopeSchema,
	cliJsonDaemonInstallEnvelopeSchema,
	cliJsonDaemonUninstallEnvelopeSchema,
	cliJsonDaemonReinstallEnvelopeSchema,
	cliJsonDaemonStatusEnvelopeSchema,
	cliJsonServiceControlCapabilitiesEnvelopeSchema,
	cliJsonDaemonStopEnvelopeSchema,
	cliJsonDaemonAutostartEnvelopeSchema,
	cliJsonAgentsDetectEnvelopeSchema,
	cliJsonAgentsListEnvelopeSchema,
	cliJsonAgentsShowEnvelopeSchema,
	cliJsonAgentsEnableEnvelopeSchema,
	cliJsonAgentsDisableEnvelopeSchema,
	cliJsonFeedbackSubmittedEnvelopeSchema,
	cliJsonProjectsListEnvelopeSchema,
	cliJsonProjectsCreateEnvelopeSchema,
	cliJsonProjectsUpdateEnvelopeSchema,
	cliJsonProjectsArchiveEnvelopeSchema,
	cliJsonProjectsBindEnvelopeSchema,
	cliJsonProjectsUnbindEnvelopeSchema,
	cliJsonProjectsConnectionsListEnvelopeSchema,
	cliJsonProjectsConnectionsSetDefaultEnvelopeSchema,
	cliJsonProjectsConnectionsRemoveEnvelopeSchema,
	cliJsonAgentActivityReadEnvelopeSchema,
	cliJsonAgentActivitySessionsEnvelopeSchema,
	cliJsonAgentActivityInstallEnvelopeSchema,
	cliJsonAgentActivityUninstallEnvelopeSchema,
	cliJsonAgentActivityStatusEnvelopeSchema,
	cliJsonAgentActivityHealthEnvelopeSchema,
	cliJsonAgentActivitySyncEnvelopeSchema,
	cliJsonAgentActivityContextEnvelopeSchema,
	cliJsonRoutinesListEnvelopeSchema,
	cliJsonRoutinesCreateEnvelopeSchema,
	cliJsonRoutinesShowEnvelopeSchema,
	cliJsonRoutinesUpdateEnvelopeSchema,
	cliJsonRoutinesPauseEnvelopeSchema,
	cliJsonRoutinesResumeEnvelopeSchema,
	cliJsonRoutinesDeleteEnvelopeSchema,
	cliJsonRoutinesRunsEnvelopeSchema,
	cliJsonSpaceMembersEnvelopeSchema,
	cliJsonSpaceMembersBriefEnvelopeSchema,
	cliJsonSpaceListOrganizationEnvelopeSchema,
	cliJsonOrganizationDirectoryEnvelopeSchema,
	cliJsonOrganizationComputersEnvelopeSchema,
	cliJsonOrganizationInviteLinkGetEnvelopeSchema,
	cliJsonOrganizationInviteLinkCreateEnvelopeSchema,
	cliJsonOrganizationInviteLinkRevokeEnvelopeSchema,
	cliJsonSkillsListEnvelopeSchema,
	cliJsonSkillsShowEnvelopeSchema,
	cliJsonSkillsLoadEnvelopeSchema
]);
/**
* The closed set of `--json` envelope `type` discriminants, in the order the
* union declares them. Useful for parity tests and contract documentation.
*/
const CLI_JSON_OUTPUT_TYPES = [
	"error",
	"daemon.run",
	"daemon.install",
	"daemon.uninstall",
	"daemon.reinstall",
	"daemon.status",
	"service.control_capabilities",
	"daemon.stop",
	"daemon.autostart",
	"agents.detect",
	"agents.list",
	"agents.show",
	"agents.enable",
	"agents.disable",
	"feedback_submitted",
	"projects.list",
	"projects.create",
	"projects.update",
	"projects.archive",
	"projects.bind",
	"projects.unbind",
	"projects.connections.list",
	"projects.connections.set_default",
	"projects.connections.remove",
	"activity.read",
	"activity.sessions",
	"activity.install",
	"activity.uninstall",
	"activity.status",
	"activity.health",
	"activity.sync",
	"activity.context",
	"routines.list",
	"routines.create",
	"routines.show",
	"routines.update",
	"routines.pause",
	"routines.resume",
	"routines.delete",
	"routines.runs",
	"space.members",
	"space.members.brief",
	"space.list.organization",
	"organization.directory",
	"organization.computers",
	"organization.invite_link.get",
	"organization.invite_link.create",
	"organization.invite_link.revoke",
	"skills.list",
	"skills.show",
	"skills.load"
];
//#endregion
export { CLI_JSON_OUTPUT_SCHEMA_VERSION, CLI_JSON_OUTPUT_TYPES, cliJsonAgentActivityContextEnvelopeSchema, cliJsonAgentActivityHealthEnvelopeSchema, cliJsonAgentActivityInstallEnvelopeSchema, cliJsonAgentActivityReadEnvelopeSchema, cliJsonAgentActivitySessionsEnvelopeSchema, cliJsonAgentActivityStatusEnvelopeSchema, cliJsonAgentActivitySyncEnvelopeSchema, cliJsonAgentActivityUninstallEnvelopeSchema, cliJsonAgentsDetectEnvelopeSchema, cliJsonAgentsDisableEnvelopeSchema, cliJsonAgentsEnableEnvelopeSchema, cliJsonAgentsListEnvelopeSchema, cliJsonAgentsShowEnvelopeSchema, cliJsonDaemonAutostartEnvelopeSchema, cliJsonDaemonInstallEnvelopeSchema, cliJsonDaemonReinstallEnvelopeSchema, cliJsonDaemonRunEnvelopeSchema, cliJsonDaemonStatusEnvelopeSchema, cliJsonDaemonStopEnvelopeSchema, cliJsonDaemonUninstallEnvelopeSchema, cliJsonEntryCheckBlockedErrorCodeSchema, cliJsonErrorEnvelopeSchema, cliJsonFeedbackSubmittedEnvelopeSchema, cliJsonOrganizationComputersEnvelopeSchema, cliJsonOrganizationDirectoryEnvelopeSchema, cliJsonOrganizationInviteLinkCreateEnvelopeSchema, cliJsonOrganizationInviteLinkGetEnvelopeSchema, cliJsonOrganizationInviteLinkRevokeEnvelopeSchema, cliJsonOutputEnvelopeSchema, cliJsonProjectsArchiveEnvelopeSchema, cliJsonProjectsBindEnvelopeSchema, cliJsonProjectsConnectionsListEnvelopeSchema, cliJsonProjectsConnectionsRemoveEnvelopeSchema, cliJsonProjectsConnectionsSetDefaultEnvelopeSchema, cliJsonProjectsCreateEnvelopeSchema, cliJsonProjectsListEnvelopeSchema, cliJsonProjectsUnbindEnvelopeSchema, cliJsonProjectsUpdateEnvelopeSchema, cliJsonRoutinesCreateEnvelopeSchema, cliJsonRoutinesDeleteEnvelopeSchema, cliJsonRoutinesListEnvelopeSchema, cliJsonRoutinesPauseEnvelopeSchema, cliJsonRoutinesResumeEnvelopeSchema, cliJsonRoutinesRunsEnvelopeSchema, cliJsonRoutinesShowEnvelopeSchema, cliJsonRoutinesUpdateEnvelopeSchema, cliJsonServiceControlCapabilitiesEnvelopeSchema, cliJsonSkillsListEnvelopeSchema, cliJsonSkillsLoadEnvelopeSchema, cliJsonSkillsShowEnvelopeSchema, cliJsonSpaceListOrganizationEnvelopeSchema, cliJsonSpaceMembersBriefEnvelopeSchema, cliJsonSpaceMembersEnvelopeSchema };

//# sourceMappingURL=json-output.js.map